import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { Users, Youtube, Instagram, Facebook, Sparkles, MoreHorizontal } from 'lucide-react';
import XLogo from './icons/XLogo';

// Brand marks lucide-react no longer ships — kept tiny and monochrome (currentColor).
const RedditIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5.01 11.03c.02.16.03.32.03.49 0 2.5-2.91 4.53-6.5 4.53s-6.5-2.03-6.5-4.53c0-.17.01-.33.03-.49a1.44 1.44 0 0 1-.82-1.3 1.45 1.45 0 0 1 2.46-1.03 7.1 7.1 0 0 1 3.86-1.22l.73-3.43a.3.3 0 0 1 .36-.23l2.43.52a1.02 1.02 0 1 1-.12.5l-2.17-.46-.65 3.06a7.1 7.1 0 0 1 3.8 1.22 1.45 1.45 0 1 1 1.6 2.33 1.44 1.44 0 0 1-.73.55zM9.25 12.5a1 1 0 1 0 2 0 1 1 0 0 0-2 0zm5.5 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0zm-.28 2.29a.3.3 0 0 0-.42 0c-.49.49-1.44.53-1.72.53s-1.23-.04-1.72-.53a.3.3 0 1 0-.42.42c.6.6 1.72.66 2.14.66s1.54-.06 2.14-.66a.3.3 0 0 0 0-.42z" />
  </svg>
);

const GoogleIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
  </svg>
);

// AI mark for ChatGPT / Claude / other — Sparkles reads as "AI" without picking a side.
export interface HeardAboutOption {
  key: string;
  label: string;
  render: () => ReactNode;
}

export const HEARD_ABOUT_OPTIONS: HeardAboutOption[] = [
  { key: 'friend', label: 'From a friend', render: () => <Users size={22} /> },
  { key: 'reddit', label: 'Reddit', render: () => <RedditIcon /> },
  { key: 'instagram', label: 'Instagram', render: () => <Instagram size={22} /> },
  { key: 'facebook', label: 'Facebook', render: () => <Facebook size={22} /> },
  { key: 'google', label: 'Google', render: () => <GoogleIcon /> },
  { key: 'ai', label: 'ChatGPT / Claude', render: () => <Sparkles size={22} /> },
  { key: 'x', label: 'X (Twitter)', render: () => <XLogo size={20} /> },
  { key: 'youtube', label: 'YouTube', render: () => <Youtube size={22} /> },
  { key: 'other', label: 'Other', render: () => <MoreHorizontal size={22} /> },
];

interface Props {
  isNight: boolean;
  onSelect: (key: string) => void;
  onSkip: () => void;
}

export default function HeardAboutPrompt({ isNight, onSelect, onSkip }: Props) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] flex items-center justify-center px-6 bg-black/40 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={`p-7 rounded-[32px] max-w-sm w-full text-center shadow-2xl border ${
          isNight ? 'bg-[#1a1a1a] border-white/10 text-white' : 'bg-white border-black/10 text-black'
        }`}
      >
        <h3 className="text-xl font-medium mb-1">Where did you hear about Zuzu?</h3>
        <p className="text-sm opacity-60 mb-6">It helps Zuzu reach more people who need this.</p>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {HEARD_ABOUT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => onSelect(opt.key)}
              className={`flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-2xl border transition-all ${
                isNight
                  ? 'bg-white/5 border-white/10 hover:bg-white/10'
                  : 'bg-black/[0.03] border-black/5 hover:bg-black/[0.06]'
              }`}
            >
              <span className="opacity-90">{opt.render()}</span>
              <span className="text-[0.6875rem] leading-tight opacity-70">{opt.label}</span>
            </button>
          ))}
        </div>

        <button
          onClick={onSkip}
          className="text-sm opacity-50 hover:opacity-80 transition-all"
        >
          Skip
        </button>
      </motion.div>
    </motion.div>
  );
}
